import { orgAPI } from "@/api/api";
import {
  ExecuteToolboxResponse,
  IntegrationConnection,
} from "@cohesive/api-client";
import type { Entity } from "@cohesive/api-entities";
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";

// A page is the full toolbox response so the entity_format="both" generic
// projections (data_generic) ride along with the typed tasks for placement.
type TasksPage = ExecuteToolboxResponse<Entity[]>;

interface UseAsanaMCPTasksSearchProps {
  connection: IntegrationConnection;
  workspaceGid?: string;
  projectGid?: string;
  isMyTasks?: boolean;
  query?: string;
  limit?: number;
  enabled?: boolean;
  options?: Partial<
    UseInfiniteQueryOptions<TasksPage, Error, InfiniteData<TasksPage>>
  >;
}

export function useAsanaMCPTasksSearch({
  connection,
  workspaceGid,
  projectGid,
  isMyTasks = false,
  query = "",
  limit = 20,
  enabled = true,
  options = {},
}: UseAsanaMCPTasksSearchProps) {
  return useInfiniteQuery({
    queryKey: [
      "asana-mcp-tasks-list",
      connection.id,
      workspaceGid,
      projectGid,
      isMyTasks,
      query || "empty",
    ],
    queryFn: async ({ pageParam }): Promise<TasksPage> => {
      // asana_search_tasks requires premium, so we use asana_get_tasks instead
      // which lists tasks by context (workspace/project/section)
      // Note: Asana API requires either project OR (assignee + workspace)
      if (!workspaceGid && !projectGid) return { data: [] };

      const actionInput: any = {
        limit,
        ...(pageParam || {}),
      };

      // If we have a project, use that (preferred)
      if (projectGid) {
        actionInput.project = projectGid;
      } else if (workspaceGid && isMyTasks) {
        // "My Tasks" node: get current user's tasks in this workspace
        actionInput.workspace = workspaceGid;
        actionInput.assignee = "me";
      } else {
        // Don't load if we're just on a workspace or team without isMyTasks
        return { data: [] };
      }

      const response = await orgAPI.toolbox.execute<Entity[]>({
        integration_connection_id: connection.id,
        action_name: "asana-mcp-get-tasks",
        action_input: actionInput,
        // Typed tasks drive the rich row UI; their generic projections ride
        // along in data_generic and are what get placed on the canvas.
        entity_format: "both",
      });

      // Client-side filtering for search query (since search API requires premium).
      // The id→generic lookup is built from the full data_generic, so filtering
      // the displayed typed tasks here doesn't affect placement resolution.
      let results = response.data;
      if (query && results) {
        const lowerQuery = query.toLowerCase();
        results = results.filter((entity: any) => {
          const name = entity.data?.name?.toLowerCase() || "";
          const content = entity.data?.content?.toLowerCase() || "";
          return name.includes(lowerQuery) || content.includes(lowerQuery);
        });
      }

      return { ...response, data: results };
    },
    getNextPageParam: () => {
      // Pagination disabled for now until we confirm structure
      return undefined;
    },
    initialPageParam: {},
    enabled:
      enabled && !!connection.integration_id && (!!projectGid || isMyTasks),
    staleTime: 1000 * 30, // 30 seconds
    ...options,
  });
}
